import type { HistoryWindow } from './useHistoryNavigation'

export const PERIOD_OPTIONS = [
  { value: '4', label: '4 hours' },
  { value: '6', label: '6 hours' },
  { value: '8', label: '8 hours' },
  { value: '12', label: '12 hours' },
  { value: '16', label: '16 hours' },
  { value: '24', label: '24 hours' },
  { value: '36', label: '36 hours' },
  { value: '48', label: '2 days' },
  { value: '72', label: '3 days' },
  { value: '120', label: '5 days' },
  { value: '168', label: '7 days' },
] as const

export interface PeriodOption {
  value: string
  label: string
}

const HOUR_TOLERANCE = 0.001

const plural = (count: number, unit: string) => `${count} ${unit}${count === 1 ? '' : 's'}`

/**
 * A span in the same style as PERIOD_OPTIONS ("36 hours", "2 days"), used for the
 * option that shows the whole picked range — naming its length rather than calling
 * it "Full Timeline", which read as the asset's entire history.
 */
export function formatPeriodLabel(hours: number): string {
  if (hours < 1) return plural(Math.max(1, Math.round(hours * 60)), 'minute')
  const wholeHours = Math.round(hours)
  if (wholeHours < 48) return plural(wholeHours, 'hour')
  const days = Math.floor(wholeHours / 24)
  const remainder = wholeHours % 24
  return remainder ? `${plural(days, 'day')} ${plural(remainder, 'hour')}` : plural(days, 'day')
}

export function buildPeriodOptions(range: HistoryWindow | null): PeriodOption[] {
  const rangeHours = range ? range.to.diff(range.from, 'hours').hours : null

  const options: PeriodOption[] = PERIOD_OPTIONS.filter((o) => !rangeHours || Number(o.value) < rangeHours).map(
    (o) => ({
      value: o.value,
      label: o.label,
    })
  )

  if (rangeHours) {
    options.push({ value: String(rangeHours), label: formatPeriodLabel(rangeHours) })
  }

  return options
}

export function findPeriodOption(options: PeriodOption[], viewHours: number): PeriodOption | undefined {
  return options.find((o) => Math.abs(Number(o.value) - viewHours) < HOUR_TOLERANCE)
}
